import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import ProgressBar from 'react-bootstrap/ProgressBar';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS as DndCss } from '@dnd-kit/utilities';

// Match the precision the question is graded at, so 70.0 doesn't display
// as bare "70" when the question asked for 1dp. Absolute tolerance picks
// dp from the tolerance value (0.5 -> 1dp, 0.05 -> 2dp); percent tolerance
// honours the natural precision of the value, capped at 2dp.
function dpFor(tolerance, value) {
  const tol = tolerance || { type: 'percent', value: 1 };
  if (tol.type === 'absolute') {
    const v = Math.abs(Number(tol.value) || 0);
    if (!Number.isFinite(v) || v <= 0) return 2;
    return Math.max(0, Math.min(4, Math.ceil(-Math.log10(v))));
  }
  if (!Number.isFinite(value)) return 2;
  const text = String(value);
  if (!text.includes('.')) return 0;
  return Math.min(2, text.split('.')[1].length);
}

function formatNumeric(value, tolerance) {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toFixed(dpFor(tolerance, num));
}

// Render a breakdown answer (submitted or expected) regardless of template
// type. Arrays (multi-mcq, cloze) join with commas; nulls fall through to em-dash.
function formatBreakdownAnswer(value, breakdown) {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '—';
  if (
    breakdown?.templateType === 'mcq'
    || breakdown?.templateType === 'true-false'
    || breakdown?.templateType === 'multi-mcq'
    || breakdown?.templateType === 'cloze'
    || breakdown?.templateType === 'ordering'
    || breakdown?.templateType === 'bucket-sort'
    || breakdown?.templateType === 'matching'
  ) {
    return String(value);
  }
  return formatNumeric(value, breakdown?.tolerance);
}

// ---------------------------------------------------------------------
// Drag-and-drop primitives for ordering + bucket-sort
// ---------------------------------------------------------------------

// Sortable list row used by the ordering question. Drag-handle pattern:
//   - Listeners attached to the whole row → desktop mouse users can grab
//     anywhere on the row (much easier than aiming at a small handle).
//   - Grip span has touch-action: none → on mobile the grip is the only
//     spot where a touch is hijacked from the browser's scroll. Touching
//     the row body lets the page scroll normally; touching the grip
//     after the TouchSensor delay starts a drag.
const SortableOrderRow = ({ id, text, index, disabled, feedback, isCorrectPosition }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: DndCss.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const stateClass = feedback
    ? (isCorrectPosition ? 'drill-order-row--correct' : 'drill-order-row--wrong')
    : '';
  return (
    <div ref={setNodeRef} style={style}
      className={`drill-order-row ${stateClass} ${disabled ? 'drill-order-row--locked' : ''}`}
      {...attributes} {...(disabled ? {} : listeners)}>
      <span className="drill-order-row__index">{index + 1}</span>
      <span className="drill-order-row__text">{text}</span>
      {!disabled ? (
        <span className="drill-order-row__grip" aria-hidden="true">
          <i className="bi bi-grip-vertical" />
        </span>
      ) : null}
    </div>
  );
};

// Item used by both the unassigned tray and inside each bucket. Same
// drag-handle pattern as SortableOrderRow: listeners on the whole item
// for easy mouse grabs, grip span has touch-action: none for mobile.
const BucketDraggableItem = ({ id, text, disabled, feedback, isCorrectPlacement }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: DndCss.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const stateClass = feedback
    ? (isCorrectPlacement ? 'drill-bucket-item--correct' : 'drill-bucket-item--wrong')
    : '';
  return (
    <div ref={setNodeRef} style={style}
      className={`drill-bucket-item ${stateClass} ${disabled ? 'drill-bucket-item--locked' : ''}`}
      {...attributes} {...(disabled ? {} : listeners)}>
      {!disabled ? (
        <span className="drill-bucket-item__grip" aria-hidden="true">
          <i className="bi bi-grip-vertical" />
        </span>
      ) : null}
      <span className="drill-bucket-item__text">{text}</span>
    </div>
  );
};

// Collision detection that snaps to the bucket the cursor is hovering
// over. closestCenter mis-targets adjacent buckets when the dragging
// item is far from the destination (its centre drifts past). pointerWithin
// is intuitive — "the bucket under your cursor" — with rectIntersection
// as a fallback for the gap between containers.
const bucketCollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  const rect = rectIntersection(args);
  if (rect.length > 0) return rect;
  return closestCenter(args);
};

// Drop target wrapper used for each bucket (and the unassigned tray).
// Receives a container id and renders its child items inside a
// SortableContext so items can also be re-ordered within the bucket.
const BucketDropZone = ({ id, label, itemIds, children, isOver }) => {
  const { setNodeRef, isOver: dropOver } = useDroppable({ id });
  const hot = isOver || dropOver;
  return (
    <div ref={setNodeRef}
      className={`drill-bucket ${hot ? 'drill-bucket--hot' : ''}`}>
      {label !== null ? <div className="drill-bucket__label">{label}</div> : null}
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className="drill-bucket__items">{children}</div>
      </SortableContext>
    </div>
  );
};

// Split a cloze prompt into alternating { type: 'text' | 'blank' } segments
// so the player can render text with inline inputs at each {{n}} marker.
// Marker numbers are 1-based; we store 0-based indices on 'blank' segments.
function parseClozeSegments(prompt) {
  const text = String(prompt || '');
  const re = /\{\{(\d+)\}\}/g;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    out.push({ type: 'blank', index: Math.max(0, Number(m[1]) - 1) });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}

// Challenge-mode points: base 10 per correct + bonus that climbs with
// the active streak (5 * streak), so a hot streak meaningfully accelerates
// the score. A wrong answer resets streak to 0 and earns nothing.
const POINTS_PER_CORRECT = 10;
const STREAK_BONUS = 5;
function pointsForCorrect(streakAfter) {
  return POINTS_PER_CORRECT + Math.max(0, streakAfter - 1) * STREAK_BONUS;
}

// Streak thresholds that trigger a confetti burst. 5 keeps it rewarding
// for a quick win; 10 + 15 keep the dopamine flowing on a hot run.
const CONFETTI_MILESTONES = new Set([5, 10, 15, 20]);
const CONFETTI_COLORS = ['#22c55e', '#facc15', '#3b82f6', '#ec4899', '#f97316', '#a855f7'];

// Seconds the auto-advance countdown waits after an answer is graded
// before flipping to the next question. Clicking the CTA at any point
// cancels the countdown and advances immediately; pressing Escape pauses
// it indefinitely. Cloze gets longer because there are multiple blanks
// + corrections to read.
const NEXT_COUNTDOWN_SECONDS = 5;
const COUNTDOWN_BY_TYPE = { cloze: 10, 'multi-mcq': 7 };
function countdownFor(question) {
  return COUNTDOWN_BY_TYPE[question?.templateType] || NEXT_COUNTDOWN_SECONDS;
}

// Big circular Next button rendered at the bottom-centre after feedback.
// The SVG ring underneath shows the auto-advance countdown ticking down.
// onAction fires on click — caller wires it to either handleNext or
// handleFinish depending on whether more questions remain.
const NextCountdownCta = ({ secondsRemaining, totalSeconds, onAction, isFinish, disabled }) => {
  // null = paused (student pressed Escape). Ring sits empty and the
  // numeric counter hides; the button still works on click.
  const isPaused = secondsRemaining === null;
  const ratio = totalSeconds > 0 && !isPaused ? Math.max(0, Math.min(1, secondsRemaining / totalSeconds)) : 0;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - ratio);
  const ariaLabel = isPaused
    ? (isFinish ? 'Finish exercise' : 'Next question')
    : (isFinish ? `Finish exercise (auto in ${Math.ceil(secondsRemaining)}s)` : `Next question (auto in ${Math.ceil(secondsRemaining)}s)`);
  return (
    <div className={`qbank-next-cta ${isPaused ? 'qbank-next-cta--paused' : ''}`}>
      <button
        type="button"
        className={`qbank-next-cta__button ${isFinish ? 'qbank-next-cta__button--finish' : ''}`}
        onClick={onAction}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        <span className="qbank-next-cta__label">{isFinish ? 'Finish' : 'Next'}</span>
        {isPaused ? null : <span className="qbank-next-cta__count">{Math.max(0, Math.ceil(secondsRemaining))}</span>}
        <i className={`qbank-next-cta__chevron bi ${isFinish ? 'bi-check-lg' : 'bi-chevron-right'}`} aria-hidden="true" />
        <svg className="qbank-next-cta__ring" viewBox="0 0 120 120" aria-hidden="true">
          <circle className="qbank-next-cta__ring-track" cx="60" cy="60" r={radius} />
          <circle
            className="qbank-next-cta__ring-progress"
            cx="60"
            cy="60"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
      </button>
      <div className="qbank-next-cta__hint">
        {isPaused
          ? `Paused — click to ${isFinish ? 'finish' : 'continue'}`
          : `Esc to pause · Enter or click to ${isFinish ? 'finish now' : 'skip ahead'}`}
      </div>
    </div>
  );
};

// Tiny full-viewport confetti overlay. No dep — every piece is a span
// with randomised CSS custom properties driven by a single keyframe.
const Confetti = ({ count = 80 }) => {
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => {
    const left = `${Math.random() * 100}vw`;
    const driftX = `${(Math.random() * 50 - 25)}vw`;
    const duration = `${1200 + Math.random() * 1000}ms`;
    const delay = `${Math.random() * 250}ms`;
    const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    return { id: i, left, driftX, duration, delay, color };
  }), [count]);
  return (
    <div className="qbank-confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="qbank-confetti__piece"
          style={{
            '--qbank-confetti-left': p.left,
            '--qbank-confetti-x': p.driftX,
            '--qbank-confetti-duration': p.duration,
            '--qbank-confetti-delay': p.delay,
            '--qbank-confetti-color': p.color,
          }}
        />
      ))}
    </div>
  );
};

const DrillSessionPlayer = ({ session, onSubmitAnswer, onSubmitSession, onRestart, onBack }) => {
  const questions = useMemo(() => session?.snapshot?.questions || [], [session]);
  const isChallenge = session?.snapshot?.mode === 'challenge';
  const previousBest = Number.isFinite(session?.personalBest?.score) ? session.personalBest.score : null;

  const [activeIndex, setActiveIndex] = useState(0);
  const [draftAnswer, setDraftAnswer] = useState('');
  // Per-question selection state for multi-mcq: { [questionId]: number[] }.
  // Stays empty for single-answer types — they post on click instead.
  const [multiSelections, setMultiSelections] = useState({});
  // Per-question cloze drafts: { [questionId]: string[] }. Each slot
  // mirrors a blank's index in the prompt; submitted as an array.
  const [clozeDrafts, setClozeDrafts] = useState({});
  // Per-question ordering drafts: { [questionId]: string[] } of item ids.
  // Per-question bucket assignments: { [questionId]: { itemId: bucketId } }.
  // Items not yet placed live in the 'unassigned' tray (no entry).
  const [orderingDrafts, setOrderingDrafts] = useState({});
  const [bucketDrafts, setBucketDrafts] = useState({});
  // Mouse: 4px drag distance activates (instant feel on desktop).
  // Touch: 200ms hold + 6px tolerance activates — lets the page scroll
  // freely with a quick swipe, only initiates drag on a deliberate hold.
  const dndSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );
  const [feedbackById, setFeedbackById] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [finalGrade, setFinalGrade] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  // Brief +X / streak animation triggered after each correct answer; we
  // bump a key to force the badge re-mount and replay the CSS animation.
  const [pulseKey, setPulseKey] = useState(0);
  // toast = the big centred "Correct!" / "Not quite!" banner that pops in
  // after every answer. Keyed by toastKey so the CSS animation re-fires
  // even when two correct answers land back-to-back.
  const [toast, setToast] = useState(null); // { kind: 'correct' | 'wrong', key: number }
  // confettiKey > 0 triggers a one-shot confetti burst. Bumping it re-
  // mounts the Confetti component (fresh randomised pieces).
  const [confettiKey, setConfettiKey] = useState(0);
  // Countdown timer (seconds remaining) for the auto-advance Next CTA.
  // null = no countdown active. Set after each answer's feedback lands.
  const [nextCountdown, setNextCountdown] = useState(null);
  const previousQuestionId = useRef(null);
  const calcInputRef = useRef(null);
  // Refs so the global keydown listener can read the latest selections /
  // submitting flag without forcing an effect re-attach on every change.
  const multiSelectionsRef = useRef({});
  const submittingRef = useRef(false);

  useEffect(() => {
    setActiveIndex(0); setFeedbackById({}); setFinalGrade(null); setDraftAnswer('');
    setMultiSelections({});
    setClozeDrafts({});
    setOrderingDrafts({});
    setBucketDrafts({});
    setScore(0); setStreak(0); setBestStreak(0);
    setToast(null); setConfettiKey(0); setNextCountdown(null);
  }, [session?.id]);

  // Auto-dismiss the toast banner after its CSS animation finishes (1100ms).
  useEffect(() => {
    if (!toast) return undefined;
    const handle = window.setTimeout(() => setToast(null), 1100);
    return () => window.clearTimeout(handle);
  }, [toast]);

  // Celebration confetti on the end-screen when the score lands in gold
  // tier (≥80%). One-shot per finalGrade arrival.
  useEffect(() => {
    if (finalGrade && finalGrade.score >= 80) {
      setConfettiKey((k) => k + 1);
    }
  }, [finalGrade]);

  const currentQuestion = questions[activeIndex];
  const totalQuestions = questions.length;

  const handleAnswerSubmit = async (overrideAnswer) => {
    if (!currentQuestion) return;
    const value = overrideAnswer !== undefined ? overrideAnswer : draftAnswer;
    setSubmitting(true);
    try {
      const result = await onSubmitAnswer(currentQuestion.id, value);
      setFeedbackById((current) => ({
        ...current,
        [currentQuestion.id]: {
          isCorrect: result.isCorrect,
          expected: result.expected,
          correctIndices: result.correctIndices,
          correctOrder: result.correctOrder,
          correctAssignments: result.correctAssignments,
          correctRightOrder: result.correctRightOrder,
          blankResults: result.blankResults,
          partialCredit: result.partialCredit,
          unit: result.unit,
          tolerance: result.tolerance,
          submitted: value,
        },
      }));
      // Pop the big centred banner regardless of mode — it's the most
      // visible bit of feedback and works as well in Learn as in Challenge.
      setToast({ kind: result.isCorrect ? 'correct' : 'wrong', key: Date.now() });
      if (isChallenge) {
        if (result.isCorrect) {
          setStreak((prev) => {
            const next = prev + 1;
            setBestStreak((b) => Math.max(b, next));
            setScore((s) => s + pointsForCorrect(next));
            // Confetti burst on milestone streaks. Bumping the key
            // re-mounts the Confetti component with fresh randomness.
            if (CONFETTI_MILESTONES.has(next)) {
              setConfettiKey((k) => k + 1);
            }
            return next;
          });
          setPulseKey((k) => k + 1);
        } else {
          setStreak(0);
        }
      }
    } catch (err) {
      setFeedbackById((current) => ({ ...current, [currentQuestion.id]: { error: err?.message || 'Could not submit answer.' } }));
    } finally { setSubmitting(false); }
  };

  const handleNext = () => {
    setDraftAnswer('');
    setNextCountdown(null);
    if (activeIndex + 1 < totalQuestions) setActiveIndex((i) => i + 1);
  };

  const handleFinish = async () => {
    setNextCountdown(null);
    setSubmitting(true);
    try {
      const result = await onSubmitSession();
      setFinalGrade(result);
    } finally { setSubmitting(false); }
  };

  // Auto-advance countdown: starts countdownFor(question) seconds after
  // feedback arrives, ticks down every 100ms, and fires handleNext /
  // handleFinish on zero. The CTA's onClick path advances immediately;
  // pressing Escape sets nextCountdown to null which the interval treats
  // as "paused" — student can then click Next manually when ready.
  const feedbackForCurrent = currentQuestion ? feedbackById[currentQuestion.id] : null;
  const isLastQuestion = activeIndex + 1 >= totalQuestions;
  const totalCountdownSeconds = countdownFor(currentQuestion);
  useEffect(() => {
    if (!feedbackForCurrent || feedbackForCurrent.error) return undefined;
    setNextCountdown(totalCountdownSeconds);
    const tickMs = 100;
    const handle = window.setInterval(() => {
      setNextCountdown((current) => {
        // null means paused (e.g. Escape pressed) — keep the handle alive
        // so the student can still see the CTA; just don't advance.
        if (current === null) return null;
        const next = current - tickMs / 1000;
        if (next <= 0) {
          window.clearInterval(handle);
          window.setTimeout(() => {
            if (isLastQuestion) handleFinish();
            else handleNext();
          }, 0);
          return 0;
        }
        return next;
      });
    }, tickMs);
    return () => window.clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackForCurrent?.isCorrect, feedbackForCurrent?.error, currentQuestion?.id, isLastQuestion, totalCountdownSeconds]);

  // Mirror latest state into refs so the keydown handler always reads
  // the current value without re-registering on every keystroke.
  useEffect(() => { multiSelectionsRef.current = multiSelections; }, [multiSelections]);
  useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  // Safety net: if activeIndex somehow advances past the last question
  // (e.g. a stale handler fires Next on the wrong question count) without
  // finalGrade being set, auto-call handleFinish so the student lands on
  // their score instead of a dead-end "no questions" screen.
  useEffect(() => {
    if (!currentQuestion && questions.length > 0 && !finalGrade && !submitting) {
      handleFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, questions.length, finalGrade, submitting]);

  // Option-key shortcuts (A/B/C/D/…) for MCQ-family questions before
  // feedback is on screen. True/false also accepts T and F. For multi-mcq
  // the key toggles the selection; for mcq + true-false it submits the
  // answer instantly (same as clicking the option). Enter on multi-mcq
  // submits the current selections (if any). Skipped when focus is in an
  // input/textarea/contenteditable so typing in cloze blanks doesn't fire.
  useEffect(() => {
    if (feedbackForCurrent || !currentQuestion) return undefined;
    const type = currentQuestion.templateType;
    if (type !== 'mcq' && type !== 'true-false' && type !== 'multi-mcq') return undefined;
    const options = Array.isArray(currentQuestion.options) ? currentQuestion.options : [];
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (submittingRef.current) return;
      if (event.key === 'Enter' && type === 'multi-mcq') {
        const selected = multiSelectionsRef.current[currentQuestion.id] || [];
        if (selected.length > 0) {
          event.preventDefault();
          handleAnswerSubmit(selected);
        }
        return;
      }
      const key = event.key.toLowerCase();
      let idx = -1;
      if (key.length === 1 && key >= 'a' && key <= 'h') {
        idx = key.charCodeAt(0) - 97;
      } else if (type === 'true-false') {
        if (key === 't') idx = 0;
        else if (key === 'f') idx = 1;
      }
      if (idx < 0 || idx >= options.length) return;
      event.preventDefault();
      if (type === 'multi-mcq') {
        setMultiSelections((current) => {
          const list = current[currentQuestion.id] || [];
          const next = list.includes(idx) ? list.filter((i) => i !== idx) : [...list, idx];
          return { ...current, [currentQuestion.id]: next };
        });
      } else {
        handleAnswerSubmit(idx);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.id, currentQuestion?.templateType, feedbackForCurrent]);

  // Re-focus the calculation input each time the question changes so the
  // student can keep typing without grabbing the mouse. autoFocus only
  // fires on initial mount; clicking Next reuses the same input element.
  // Skipped for MCQ/true-false/multi-mcq (no text input rendered).
  useEffect(() => {
    if (currentQuestion?.templateType && currentQuestion.templateType !== 'calculation') return;
    if (feedbackForCurrent) return;
    if (calcInputRef.current) calcInputRef.current.focus();
  }, [currentQuestion?.id, currentQuestion?.templateType, feedbackForCurrent]);

  // Keyboard shortcuts while feedback is on screen:
  //   Enter — same Next/Finish action as the big CTA
  //   Escape — pause the auto-advance countdown so the student can review
  //            longer. Click the CTA to advance manually.
  // Enter is skipped when the user is typing in a textarea / contenteditable
  // so we don't intercept a submit. The calculation form already submits
  // on Enter via its own <form>.
  useEffect(() => {
    if (!feedbackForCurrent || feedbackForCurrent.error) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setNextCountdown(null);
        return;
      }
      if (event.key !== 'Enter') return;
      const tag = event.target?.tagName;
      if (tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      event.preventDefault();
      if (isLastQuestion) handleFinish(); else handleNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackForCurrent?.isCorrect, feedbackForCurrent?.error, currentQuestion?.id, isLastQuestion]);

  // -----------------------------------------------------------------------
  // End-of-exercise screen — celebration card for Challenge mode, full
  // question breakdown for Learn mode.
  // -----------------------------------------------------------------------
  if (finalGrade) {
    if (isChallenge) {
      const newBest = previousBest === null ? true : finalGrade.score > previousBest;
      const tier = finalGrade.score >= 80 ? 'gold' : finalGrade.score >= 50 ? 'silver' : 'bronze';
      const tierLabel = { gold: 'Excellent', silver: 'Solid effort', bronze: 'Keep practising' }[tier];
      return (
        <div className="student-page student-page--bright challenge-page">
          {confettiKey > 0 ? <Confetti key={`celeb-${confettiKey}`} count={140} /> : null}
          <Container className="mt-4 challenge-celebration">
            <Card className="challenge-celebration__card">
              <Card.Body className="text-center">
                <div className={`challenge-celebration__medal challenge-celebration__medal--${tier}`}>
                  <i className="bi bi-trophy-fill" aria-hidden="true" />
                </div>
                <h2 className="challenge-celebration__title">{tierLabel}</h2>
                {newBest ? <Badge bg="warning" text="dark" className="challenge-celebration__newbest"><i className="bi bi-stars me-1" />New personal best</Badge> : null}
                <div className="challenge-celebration__score">{finalGrade.score}%</div>
                <div className="challenge-celebration__sub text-muted">
                  {finalGrade.breakdown.filter((b) => b.isCorrect).length} of {finalGrade.breakdown.length} correct · {score} pts · best streak {bestStreak}
                </div>
                {previousBest !== null && !newBest ? (
                  <div className="small text-muted mt-2">Your personal best is still {previousBest}%.</div>
                ) : null}
                <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
                  <Button type="button" variant="primary" size="lg" onClick={onRestart}>
                    <i className="bi bi-arrow-repeat me-1" /> Play again
                  </Button>
                  <Button type="button" variant="outline-secondary" size="lg" onClick={onBack}>Back to exercises</Button>
                </div>
              </Card.Body>
            </Card>
            <details className="challenge-celebration__details mt-3">
              <summary className="small text-muted">Show question breakdown</summary>
              <div className="mt-2">
                {finalGrade.breakdown.map((b, i) => {
                  const partial = Number.isFinite(b.partialCredit) ? b.partialCredit : (b.isCorrect ? 1 : 0);
                  const borderClass = b.isCorrect ? 'border-success' : (partial > 0 ? 'border-warning' : 'border-danger');
                  const badgeBg = b.isCorrect ? 'success' : (partial > 0 ? 'warning' : 'danger');
                  const badgeText = b.isCorrect ? 'Correct' : (partial > 0 ? `${Math.round(partial * 100)}%` : 'Incorrect');
                  return (
                    <Card key={b.questionId || i} className={`mb-2 ${borderClass}`}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                          <div className="me-3">
                            <div className="small text-muted">Question {i + 1}</div>
                            <div>{b.prompt}</div>
                          </div>
                          <Badge bg={badgeBg} text={partial > 0 && !b.isCorrect ? 'dark' : undefined}>{badgeText}</Badge>
                        </div>
                        <div className="small mt-2">Your answer: <strong>{formatBreakdownAnswer(b.submittedAnswer ?? b.submittedNumeric, b)}</strong>{' '}
                          {b.unit ? <span className="text-muted">{b.unit}</span> : null}</div>
                        <div className="small">Correct answer: <strong>{formatBreakdownAnswer(b.expected, b)}</strong>{' '}
                          {b.unit ? <span className="text-muted">{b.unit}</span> : null}</div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </details>
          </Container>
        </div>
      );
    }
    return (
      <div className="student-page">
        <Container className="mt-4">
          <Card className="container-shadow">
            <Card.Body>
              <h2>Exercise complete</h2>
              <p className="text-muted">You scored <strong>{finalGrade.score}%</strong> ({finalGrade.breakdown.filter((b) => b.isCorrect).length} of {finalGrade.breakdown.length} correct).</p>
              <ProgressBar now={finalGrade.score} variant={finalGrade.score >= 80 ? 'success' : finalGrade.score >= 50 ? 'warning' : 'danger'} className="mb-3" />
              <h5>Question breakdown</h5>
              {finalGrade.breakdown.map((b, i) => {
                const partial = Number.isFinite(b.partialCredit) ? b.partialCredit : (b.isCorrect ? 1 : 0);
                const borderClass = b.isCorrect ? 'border-success' : (partial > 0 ? 'border-warning' : 'border-danger');
                const badgeBg = b.isCorrect ? 'success' : (partial > 0 ? 'warning' : 'danger');
                const badgeText = b.isCorrect ? 'Correct' : (partial > 0 ? `${Math.round(partial * 100)}%` : 'Incorrect');
                const typeLabel = b.templateType === 'mcq' ? ' · multiple choice'
                  : b.templateType === 'true-false' ? ' · true / false'
                  : b.templateType === 'multi-mcq' ? ' · multi-select' : '';
                return (
                  <Card key={b.questionId || i} className={`mb-2 ${borderClass}`}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                        <div className="me-3">
                          <div className="small text-muted">Question {i + 1}{typeLabel}</div>
                          <div>{b.prompt}</div>
                        </div>
                        <Badge bg={badgeBg} text={partial > 0 && !b.isCorrect ? 'dark' : undefined}>{badgeText}</Badge>
                      </div>
                      <div className="small mt-2">Your answer: <strong>{formatBreakdownAnswer(b.submittedAnswer ?? b.submittedNumeric, b)}</strong>{' '}
                        {b.unit ? <span className="text-muted">{b.unit}</span> : null}</div>
                      <div className="small">Correct answer: <strong>{formatBreakdownAnswer(b.expected, b)}</strong>{' '}
                        {b.unit ? <span className="text-muted">{b.unit}</span> : null}</div>
                    </Card.Body>
                  </Card>
                );
              })}
              <div className="d-flex gap-2 mt-3">
                <Button type="button" variant="primary" onClick={onRestart}>Try a new set</Button>
                <Button type="button" variant="outline-secondary" onClick={onBack}>Back to exercises</Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="student-page">
        <Container className="mt-4 text-center">
          {questions.length ? (
            <>
              <p className="text-muted">Tallying your score…</p>
              <Button type="button" variant="primary" onClick={handleFinish} disabled={submitting}>
                {submitting ? 'Checking…' : 'Show results'}
              </Button>
            </>
          ) : (
            <Alert variant="warning">No questions available in this session.</Alert>
          )}
        </Container>
      </div>
    );
  }

  const feedback = feedbackById[currentQuestion.id];
  const progress = totalQuestions ? Math.round(((activeIndex + 1) / totalQuestions) * 100) : 0;

  // -----------------------------------------------------------------------
  // In-session header. Challenge mode shows a sticky scoreboard with the
  // running score and current streak; Learn mode keeps the calm header.
  // -----------------------------------------------------------------------
  const header = isChallenge ? (
    <div className="challenge-scoreboard">
      <Container className="challenge-scoreboard__inner">
        <div className="challenge-scoreboard__row">
          <div className="challenge-scoreboard__brand">
            {onBack ? (
              <Button type="button" variant="link" className="challenge-scoreboard__back" onClick={onBack}>
                <i className="bi bi-x-lg" /> Exit
              </Button>
            ) : null}
            <div>
              <div className="challenge-scoreboard__title">{session?.drillTitle || 'Exercise'}</div>
              <div className="challenge-scoreboard__progress">Question {activeIndex + 1} of {totalQuestions}</div>
            </div>
          </div>
          <div className="challenge-scoreboard__stats">
            <div className="challenge-scoreboard__stat">
              <div className="challenge-scoreboard__stat-label">Streak</div>
              <div key={`streak-${pulseKey}`} className={`challenge-scoreboard__stat-value ${streak > 0 ? 'challenge-scoreboard__stat-value--hot challenge-scoreboard__pulse' : ''}`}>
                {streak > 0 ? <i className="bi bi-fire" /> : <i className="bi bi-circle" />}{' '}{streak}
              </div>
            </div>
            <div className="challenge-scoreboard__stat">
              <div className="challenge-scoreboard__stat-label">Score</div>
              <div key={`score-${pulseKey}`} className="challenge-scoreboard__stat-value challenge-scoreboard__pulse">{score}</div>
            </div>
            {previousBest !== null ? (
              <div className="challenge-scoreboard__stat challenge-scoreboard__stat--muted">
                <div className="challenge-scoreboard__stat-label">Best</div>
                <div className="challenge-scoreboard__stat-value">{previousBest}%</div>
              </div>
            ) : null}
          </div>
        </div>
        <ProgressBar now={progress} className="challenge-scoreboard__bar" />
      </Container>
    </div>
  ) : (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">{session?.drillTitle || 'Exercise'}</h2>
          <p className="text-muted mb-0">Question {activeIndex + 1} of {totalQuestions}</p>
        </div>
        {onBack ? <Button type="button" variant="outline-secondary" onClick={onBack}>Exit exercise</Button> : null}
      </div>
      <ProgressBar now={progress} className="mb-3" />
    </Container>
  );

  return (
    <div className={`student-page student-page--bright ${isChallenge ? 'challenge-page' : ''}`}>
      {header}
      <Container className="mt-4 drill-question-container">
        <Card className={`drill-question-card mb-3 ${isChallenge ? 'challenge-question' : ''}`}>
          <Card.Body className="drill-question-body">
            {currentQuestion.framing === 'best' || currentQuestion.framing === 'worst' ? (
              <div className={`drill-framing-banner drill-framing-banner--${currentQuestion.framing}`}>
                <i className="bi bi-megaphone-fill me-2" aria-hidden="true" />
                Pick the <strong>{currentQuestion.framing.toUpperCase()}</strong> answer
              </div>
            ) : null}
            {currentQuestion.templateType !== 'cloze' ? (
              <p className="drill-question-prompt">{currentQuestion.prompt}</p>
            ) : null}
            {currentQuestion.templateType === 'mcq' || currentQuestion.templateType === 'true-false' ? (
              <div className={`d-grid gap-2 ${currentQuestion.templateType === 'true-false' ? 'drill-true-false-grid' : ''}`}>
                {(currentQuestion.options || []).map((option, optionIndex) => {
                  const submittedIdx = feedback ? Number(feedback.submitted) : null;
                  const isSubmitted = feedback && submittedIdx === optionIndex;
                  const isCorrectOption = feedback && optionIndex === currentQuestion.correctIndex;
                  const variant = !feedback ? 'outline-primary'
                    : isCorrectOption ? 'success'
                    : isSubmitted ? 'danger'
                    : 'outline-secondary';
                  return (
                    <Button key={optionIndex} type="button" size="lg" variant={variant}
                      className={currentQuestion.templateType === 'true-false' ? 'drill-true-false-option' : ''}
                      onClick={() => handleAnswerSubmit(optionIndex)} disabled={Boolean(feedback) || submitting}>
                      {currentQuestion.templateType === 'true-false'
                        ? <span className="fw-bold">{option}</span>
                        : <><span className="me-2 fw-bold">{String.fromCharCode(65 + optionIndex)}.</span>{option}</>}
                    </Button>
                  );
                })}
              </div>
            ) : currentQuestion.templateType === 'multi-mcq' ? (() => {
              const selected = multiSelections[currentQuestion.id] || [];
              const correctSet = new Set(
                feedback && Array.isArray(feedback.correctIndices) ? feedback.correctIndices
                : (Array.isArray(currentQuestion.correctIndices) ? currentQuestion.correctIndices : [])
              );
              const submittedSet = new Set(
                feedback && Array.isArray(feedback.submitted) ? feedback.submitted : selected
              );
              const toggle = (idx) => {
                if (feedback) return;
                setMultiSelections((current) => {
                  const list = current[currentQuestion.id] || [];
                  const next = list.includes(idx) ? list.filter((i) => i !== idx) : [...list, idx];
                  return { ...current, [currentQuestion.id]: next };
                });
              };
              return (
                <>
                  <div className="d-grid gap-2">
                    {(currentQuestion.options || []).map((option, optionIndex) => {
                      const isSelected = submittedSet.has(optionIndex);
                      const isCorrectOption = feedback && correctSet.has(optionIndex);
                      const wasWrongPick = feedback && isSelected && !correctSet.has(optionIndex);
                      const variant = !feedback
                        ? (isSelected ? 'primary' : 'outline-primary')
                        : isCorrectOption ? 'success'
                        : wasWrongPick ? 'danger'
                        : 'outline-secondary';
                      return (
                        <Button key={optionIndex} type="button" size="lg" variant={variant}
                          className="drill-multi-option text-start"
                          onClick={() => toggle(optionIndex)} disabled={Boolean(feedback) || submitting}>
                          <i className={`bi ${isSelected ? 'bi-check-square-fill' : 'bi-square'} me-2`} aria-hidden="true" />
                          <span className="me-2 fw-bold">{String.fromCharCode(65 + optionIndex)}.</span>{option}
                        </Button>
                      );
                    })}
                  </div>
                  {!feedback ? (
                    <div className="d-flex justify-content-center mt-3">
                      <Button type="button" variant="primary" size="lg"
                        onClick={() => handleAnswerSubmit(selected)}
                        disabled={submitting || selected.length === 0}>
                        {submitting ? 'Checking…' : 'Submit selections'}
                      </Button>
                    </div>
                  ) : null}
                </>
              );
            })() : currentQuestion.templateType === 'matching' ? (() => {
              const leftColumn = Array.isArray(currentQuestion.leftColumn) ? currentQuestion.leftColumn : [];
              const rightItems = Array.isArray(currentQuestion.rightItems) ? currentQuestion.rightItems : [];
              const rightMap = Object.fromEntries(rightItems.map((it) => [it.id, it.text]));
              const draft = orderingDrafts[currentQuestion.id] || rightItems.map((it) => it.id);
              const correctOrder = Array.isArray(currentQuestion.correctRightOrder)
                ? currentQuestion.correctRightOrder
                : (Array.isArray(feedback?.correctRightOrder) ? feedback.correctRightOrder : []);
              const handleDragEnd = (event) => {
                if (feedback) return;
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = draft.indexOf(active.id);
                const newIndex = draft.indexOf(over.id);
                if (oldIndex < 0 || newIndex < 0) return;
                setOrderingDrafts((cur) => ({ ...cur, [currentQuestion.id]: arrayMove(draft, oldIndex, newIndex) }));
              };
              return (
                <>
                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <div className="drill-match-board">
                      <div className="drill-match-column drill-match-column--left">
                        {leftColumn.map((left) => (
                          <div key={left.id} className="drill-match-cell drill-match-cell--left">
                            <span className="drill-match-cell__text">{left.text}</span>
                          </div>
                        ))}
                      </div>
                      <div className="drill-match-column drill-match-column--right">
                        <SortableContext items={draft} strategy={verticalListSortingStrategy}>
                          {draft.map((id, idx) => (
                            <SortableOrderRow
                              key={id}
                              id={id}
                              text={rightMap[id] || ''}
                              index={idx}
                              disabled={Boolean(feedback)}
                              feedback={Boolean(feedback)}
                              isCorrectPosition={correctOrder[idx] === id}
                            />
                          ))}
                        </SortableContext>
                      </div>
                    </div>
                  </DndContext>
                  {!feedback ? (
                    <div className="d-flex justify-content-center mt-3">
                      <Button type="button" variant="primary" size="lg"
                        onClick={() => handleAnswerSubmit(draft)}
                        disabled={submitting}>
                        {submitting ? 'Checking…' : 'Submit matches'}
                      </Button>
                    </div>
                  ) : null}
                </>
              );
            })() : currentQuestion.templateType === 'ordering' ? (() => {
              const items = Array.isArray(currentQuestion.items) ? currentQuestion.items : [];
              const itemMap = Object.fromEntries(items.map((it) => [it.id, it.text]));
              const draft = orderingDrafts[currentQuestion.id] || items.map((it) => it.id);
              const correctOrder = Array.isArray(currentQuestion.correctOrder)
                ? currentQuestion.correctOrder
                : (Array.isArray(feedback?.correctOrder) ? feedback.correctOrder : []);
              const handleDragEnd = (event) => {
                if (feedback) return;
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = draft.indexOf(active.id);
                const newIndex = draft.indexOf(over.id);
                if (oldIndex < 0 || newIndex < 0) return;
                setOrderingDrafts((cur) => ({ ...cur, [currentQuestion.id]: arrayMove(draft, oldIndex, newIndex) }));
              };
              return (
                <>
                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={draft} strategy={verticalListSortingStrategy}>
                      <div className="drill-order-list">
                        {draft.map((id, idx) => (
                          <SortableOrderRow
                            key={id}
                            id={id}
                            text={itemMap[id] || ''}
                            index={idx}
                            disabled={Boolean(feedback)}
                            feedback={Boolean(feedback)}
                            isCorrectPosition={correctOrder[idx] === id}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {!feedback ? (
                    <div className="d-flex justify-content-center mt-3">
                      <Button type="button" variant="primary" size="lg"
                        onClick={() => handleAnswerSubmit(draft)}
                        disabled={submitting}>
                        {submitting ? 'Checking…' : 'Submit order'}
                      </Button>
                    </div>
                  ) : null}
                </>
              );
            })() : currentQuestion.templateType === 'bucket-sort' ? (() => {
              const items = Array.isArray(currentQuestion.items) ? currentQuestion.items : [];
              const buckets = Array.isArray(currentQuestion.buckets) ? currentQuestion.buckets : [];
              const itemMap = Object.fromEntries(items.map((it) => [it.id, it.text]));
              const assignments = bucketDrafts[currentQuestion.id] || {};
              const correctAssignments = currentQuestion.correctAssignments
                || (feedback && feedback.correctAssignments) || {};
              const unassigned = items.map((it) => it.id).filter((id) => !assignments[id]);
              const inBucket = (bucketId) => items.map((it) => it.id).filter((id) => assignments[id] === bucketId);
              const allItemsPlaced = unassigned.length === 0;
              const handleDragEnd = (event) => {
                if (feedback) return;
                const { active, over } = event;
                if (!over) return;
                const activeId = String(active.id);
                const overId = String(over.id);
                // Drop target is either a bucket id, the unassigned tray
                // id, or another item id (sortable target inside its
                // container). Resolve to the destination container id.
                let destContainer;
                if (overId === 'unassigned') destContainer = null;
                else if (buckets.some((b) => b.id === overId)) destContainer = overId;
                else {
                  // overId is an item id — its container is wherever the
                  // assignments map currently puts it.
                  destContainer = assignments[overId] || null;
                }
                setBucketDrafts((cur) => {
                  const next = { ...(cur[currentQuestion.id] || {}) };
                  if (destContainer === null) {
                    delete next[activeId];
                  } else {
                    next[activeId] = destContainer;
                  }
                  return { ...cur, [currentQuestion.id]: next };
                });
              };
              return (
                <>
                  <DndContext sensors={dndSensors} collisionDetection={bucketCollisionDetection} onDragEnd={handleDragEnd}>
                    <div className="drill-bucket-board">
                      <BucketDropZone id="unassigned" label="To place" itemIds={unassigned}>
                        {unassigned.length === 0 ? (
                          <div className="text-muted small text-center py-2">All items placed.</div>
                        ) : unassigned.map((id) => (
                          <BucketDraggableItem key={id} id={id} text={itemMap[id] || ''} disabled={Boolean(feedback)} />
                        ))}
                      </BucketDropZone>
                      <div className="drill-bucket-board__columns">
                        {buckets.map((b) => {
                          const ids = inBucket(b.id);
                          return (
                            <BucketDropZone key={b.id} id={b.id} label={b.label} itemIds={ids}>
                              {ids.length === 0 ? (
                                <div className="text-muted small text-center py-2">Drop here</div>
                              ) : ids.map((id) => (
                                <BucketDraggableItem
                                  key={id}
                                  id={id}
                                  text={itemMap[id] || ''}
                                  disabled={Boolean(feedback)}
                                  feedback={Boolean(feedback)}
                                  isCorrectPlacement={correctAssignments[id] === b.id}
                                />
                              ))}
                            </BucketDropZone>
                          );
                        })}
                      </div>
                    </div>
                  </DndContext>
                  {!feedback ? (
                    <div className="d-flex justify-content-center mt-3">
                      <Button type="button" variant="primary" size="lg"
                        onClick={() => handleAnswerSubmit(assignments)}
                        disabled={submitting || !allItemsPlaced}>
                        {submitting ? 'Checking…' : allItemsPlaced ? 'Submit placements' : `Place all items (${unassigned.length} left)`}
                      </Button>
                    </div>
                  ) : null}
                </>
              );
            })() : currentQuestion.templateType === 'cloze' ? (() => {
              const blanks = Array.isArray(currentQuestion.blanks) ? currentQuestion.blanks : [];
              const draft = clozeDrafts[currentQuestion.id] || blanks.map(() => '');
              const segments = parseClozeSegments(currentQuestion.prompt);
              const updateBlank = (blankIdx, value) => {
                if (feedback) return;
                setClozeDrafts((current) => {
                  const list = current[currentQuestion.id] || blanks.map(() => '');
                  const next = list.slice();
                  next[blankIdx] = value;
                  return { ...current, [currentQuestion.id]: next };
                });
              };
              const blankResults = Array.isArray(feedback?.blankResults) ? feedback.blankResults : [];
              return (
                <Form onSubmit={(event) => {
                  event.preventDefault();
                  if (feedback) return;
                  const list = clozeDrafts[currentQuestion.id] || blanks.map(() => '');
                  handleAnswerSubmit(list.slice(0, blanks.length));
                }}>
                  <div className="drill-cloze-prompt">
                    {segments.map((seg, i) => {
                      if (seg.type === 'text') {
                        return <span key={`t-${i}`} className="drill-cloze-text">{seg.value}</span>;
                      }
                      const blankIdx = seg.index;
                      const value = draft[blankIdx] || '';
                      const result = blankResults[blankIdx];
                      const cls = !feedback
                        ? 'drill-cloze-blank'
                        : (result?.isCorrect ? 'drill-cloze-blank drill-cloze-blank--correct' : 'drill-cloze-blank drill-cloze-blank--wrong');
                      return (
                        <span key={`b-${i}`} className="drill-cloze-blank-wrap">
                          <input type="text"
                            className={cls}
                            value={value}
                            onChange={(e) => updateBlank(blankIdx, e.target.value)}
                            disabled={Boolean(feedback) || submitting}
                            aria-label={`Blank ${blankIdx + 1}`}
                            size={Math.max(6, value.length || 6)}
                          />
                          {feedback && result && !result.isCorrect ? (
                            <span className="drill-cloze-correction small ms-1" aria-label={`correct answer: ${result.expected}`}>
                              <i className="bi bi-arrow-right-short me-0" aria-hidden="true" />{result.expected}
                            </span>
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                  {!feedback ? (
                    <div className="d-flex justify-content-center mt-3">
                      <Button type="submit" variant="primary" size="lg"
                        disabled={submitting || (draft.every((v) => !String(v || '').trim()))}>
                        {submitting ? 'Checking…' : 'Submit'}
                      </Button>
                    </div>
                  ) : null}
                </Form>
              );
            })() : (
              <Form onSubmit={(event) => { event.preventDefault(); if (!feedback) handleAnswerSubmit(); }}>
                <div className="drill-answer-row">
                  <InputGroup size="lg">
                    <Form.Control
                      ref={calcInputRef}
                      type="text"
                      inputMode="decimal"
                      placeholder="Your answer"
                      value={draftAnswer}
                      onChange={(e) => setDraftAnswer(e.target.value)}
                      disabled={Boolean(feedback) || submitting}
                      autoFocus
                    />
                    {currentQuestion.unit ? <InputGroup.Text>{currentQuestion.unit}</InputGroup.Text> : null}
                    {!feedback ? (
                      <Button type="submit" variant="primary"
                        disabled={submitting || !String(draftAnswer || '').trim()}>
                        {submitting ? 'Checking…' : 'Submit'}
                      </Button>
                    ) : null}
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Tolerance: {currentQuestion.tolerance?.value || 1}{currentQuestion.tolerance?.type === 'percent' ? '%' : ''}.
                    {currentQuestion.unit ? ` You may type the unit (e.g. "50 ${currentQuestion.unit}").` : ''}
                  </Form.Text>
                </div>
              </Form>
            )}
            {feedback ? (
              feedback.error ? (
                <div className="qbank-feedback qbank-feedback--error mt-3">
                  <i className="qbank-feedback__icon bi bi-exclamation-octagon-fill" aria-hidden="true" />
                  <div className="qbank-feedback__body">{feedback.error}</div>
                </div>
              ) : (() => {
                const isMulti = currentQuestion.templateType === 'multi-mcq';
                const isCloze = currentQuestion.templateType === 'cloze';
                const isOrdering = currentQuestion.templateType === 'ordering';
                const isBucketSort = currentQuestion.templateType === 'bucket-sort';
                const isMatching = currentQuestion.templateType === 'matching';
                const partial = Number.isFinite(feedback.partialCredit) ? feedback.partialCredit : (feedback.isCorrect ? 1 : 0);
                const hasPartial = (isMulti || isCloze || isOrdering || isBucketSort || isMatching) && partial > 0 && !feedback.isCorrect;
                const tone = feedback.isCorrect ? 'correct' : (hasPartial ? 'partial' : 'wrong');
                const heading = feedback.isCorrect
                  ? 'Correct'
                  : (hasPartial ? `Partial credit — ${Math.round(partial * 100)}%` : 'Not quite');
                const icon = feedback.isCorrect
                  ? 'bi-check-circle-fill'
                  : (hasPartial ? 'bi-pie-chart-fill' : 'bi-x-circle-fill');
                return (
                  <div className={`qbank-feedback qbank-feedback--${tone} mt-3`} role="status" aria-live="polite">
                    <i className={`qbank-feedback__icon bi ${icon}`} aria-hidden="true" />
                    <div className="qbank-feedback__body">
                      <div className="qbank-feedback__heading">{heading}</div>
                      <div className="qbank-feedback__detail">
                        {currentQuestion.templateType === 'mcq' || currentQuestion.templateType === 'true-false' ? (
                          <>Correct answer: <strong>{currentQuestion.options?.[currentQuestion.correctIndex] ?? feedback.expected}</strong></>
                        ) : isMulti ? (
                          <>Correct answers: <strong>{
                            (currentQuestion.correctIndices || [])
                              .map((i) => currentQuestion.options?.[i])
                              .filter(Boolean)
                              .join(', ') || (Array.isArray(feedback.expected) ? feedback.expected.join(', ') : '—')
                          }</strong></>
                        ) : isCloze ? (
                          <>{feedback.isCorrect ? 'All blanks correct.' : 'Correct answers shown alongside each blank above.'}</>
                        ) : isOrdering ? (
                          <>{feedback.isCorrect ? 'Order correct.' : 'Correct positions highlighted green above; wrong positions red.'}</>
                        ) : isBucketSort ? (
                          <>{feedback.isCorrect ? 'All items in the right bucket.' : 'Correct placements highlighted green above; wrong ones red.'}</>
                        ) : isMatching ? (
                          <>{feedback.isCorrect ? 'All pairs matched correctly.' : 'Correct matches highlighted green; wrong rows red. The left column was fixed.'}</>
                        ) : (
                          <>Expected: <strong>{formatNumeric(feedback.expected, feedback.tolerance ?? currentQuestion.tolerance)}{currentQuestion.unit ? ` ${currentQuestion.unit}` : ''}</strong>{' '}
                            <span className="text-muted">(you answered: {feedback.submitted}{currentQuestion.unit ? ` ${currentQuestion.unit}` : ''})</span></>
                        )}
                      </div>
                      {currentQuestion.notes ? (
                        <div className="qbank-feedback__notes">{currentQuestion.notes}</div>
                      ) : null}
                    </div>
                  </div>
                );
              })()
            ) : null}
          </Card.Body>
        </Card>
        {feedback && !feedback.error ? (
          <NextCountdownCta
            secondsRemaining={nextCountdown}
            totalSeconds={totalCountdownSeconds}
            onAction={isLastQuestion ? handleFinish : handleNext}
            isFinish={isLastQuestion}
            disabled={submitting}
          />
        ) : null}
      </Container>
      {toast ? (
        <div
          key={toast.key}
          className={`qbank-toast qbank-toast--${toast.kind}`}
          role="status"
          aria-live="polite"
        >
          <i className={`qbank-toast__icon bi ${toast.kind === 'correct' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} aria-hidden="true" />
          {toast.kind === 'correct' ? 'Correct!' : 'Not quite'}
        </div>
      ) : null}
      {confettiKey > 0 ? <Confetti key={confettiKey} /> : null}
    </div>
  );
};

export default DrillSessionPlayer;
